<template>
  <div class="modal-content fade-in" ref="scoreModal">
    <!-- Tabs -->
    <div class="tabs">
      <div
        :class="['tab', { active: activeTab === 'achievements' }]"
        @click="setActiveTab('achievements')"
      >
        <span class="tab-text">Achievements</span>
        <span class="tab-icon">
          <AwardIcon />
        </span>
      </div>
      <div
        :class="['tab', { active: activeTab === 'scoreDistribution' }]"
        @click="setActiveTab('scoreDistribution')"
      >
        <span class="tab-text">Score Distribution</span>
        <span class="tab-icon">
          <ChartIcon />
        </span>
      </div>
      <div
        :class="['tab', { active: activeTab === 'gameDetails' }]"
        @click="setActiveTab('gameDetails')"
      >
        <span class="tab-text">Game Summary</span>
        <span class="tab-icon">
          <GridIcon />
        </span>
      </div>
    </div>

    <!-- Content Area -->
    <div class="tab-content" v-if="loading">
      <div class="loading-spinner">
        <svg class="spinner" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="4" />
        </svg>
      </div>
    </div>
    <div class="tab-content" v-else>
      <!-- Achievements Tab Content -->
      <achievements-tab
        v-if="activeTab === 'achievements'"
        :boardId="boardId"
        :board="board"
        :archiveMode="archiveMode"
        :score="score"
        :rank="rank"
        :plays="plays"
        :statsData="statsData"
        :coordsAndRarities="coordsAndRarities"
        :rarityPercentages="rarityPercentages"
        :hasMadeGuessInInfiniteMode="hasMadeGuessInInfiniteMode"
        :hasChangedScoreModalTab="hasChangedScoreModalTab"
        @select-game-summary="setActiveTab('gameDetails')"
        @select-score-distribution="setActiveTab('scoreDistribution')"
      />

      <!-- Score Distribution Tab Content -->
      <score-distribution-tab
        v-if="activeTab === 'scoreDistribution'"
        :boardId="boardId"
        :board="board"
        :score="score"
        :rank="rank"
        :plays="plays"
        :statsData="statsData"
        :coordsAndRarities="coordsAndRarities"
        :rarityPercentages="rarityPercentages"
        :hasMadeGuessInInfiniteMode="hasMadeGuessInInfiniteMode"
      />

      <!-- Game Details Tab Content -->
      <game-details-tab
        v-if="activeTab === 'gameDetails'"
        :score="score"
        :boardId="boardId"
        :board="board"
        :plays="plays"
        :totalPlays="totalPlays"
        :liveRarityData="liveRarityData"
        :gameData="gameData"
        :countryData="countryData"
        :answersList="answersList"
        :wrongGuesses="wrongGuesses"
      />
    </div>
  </div>
</template>

<script>
  /* eslint-disable vue/no-unused-components */
  import axios from 'axios';
  import popularAnswerBoard from '../boards/popularAnswerBoard.vue';
  import answerFreq from '../boards/answerFreq.vue';
  import showAnswersBoard from '../boards/showAnswersBoard.vue';
  import leastCommonAnswers from '../boards/leastCommonAnswers.vue';
  import wrongAnswersBoard from '../boards/wrongAnswersBoard.vue';
  import scoreDistributionTab from './scoreDistributionTab.vue';
  import achievementsTab from './achievementsTab.vue';
  import gameDetailsTab from './gameDetailsTab.vue';
  import { AwardIcon, ChartIcon, GridIcon } from '@/components/svgs';
  import config from '../../../axiospath.js';
  import { saveBoardToLocalStorage, loadBoardFromLocalStorage } from '@/utils/storageUtils';

  export default {
    components: {
      popularAnswerBoard,
      answerFreq,
      showAnswersBoard,
      leastCommonAnswers,
      wrongAnswersBoard,
      scoreDistributionTab,
      achievementsTab,
      gameDetailsTab,
      AwardIcon,
      ChartIcon,
      GridIcon,
    },
    props: [
      'score',
      'boardId',
      'board',
      'archiveMode',
      'liveRarityData',
      'gameData',
      'countryData',
      'answersList',
      'wrongGuesses',
      'hasMadeGuessInInfiniteMode',
      'extendedPlay',
      'rarityPercentages',
    ],
    data() {
      return {
        loading: true,
        rankData: null,
        statsData: null,
        localLiveRarityData: JSON.parse(localStorage.getItem(`answer_freq_data_${this.boardId}`)),
        activeIndexes: [],
        coordsAndRarities: [],
        activeTab: 'achievements',
        showNotification: true,
        hasChangedScoreModalTab: localStorage.getItem('hasChangedScoreModalTab') === 'true',
      };
    },
    computed: {
      rank() {
        return this.rankData?.rank_for_mode || null;
      },
      plays() {
        return this.rankData?.num_scores_for_mode || null;
      },
      totalPlays() {
        return this.rankData?.plays || null;
      },
    },
    methods: {
      getBoardKey() {
        return this.boardId + (this.extendedPlay ? 'e' : '');
      },
      formatNumber(number) {
        if (number === null) return ''; // Return an empty string if the number is null
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      },
      // Extended play
      canContinueInExtendedPlay() {
        return false;
        /*
        Work in progress:
        let atLeastOneMissing = false;
        if (this.rarityPercentages.length < 3) {
          atLeastOneMissing = true;
        } else {
          for (let i = 0; i < this.rarityPercentages.length; i++) {
            if (!this.rarityPercentages[i]) {
              atLeastOneMissing = true;
              break;
            }
            if (this.rarityPercentages[i].length < 3) {
              atLeastOneMissing = true;
              break;
            }
            for (let j = 0; j < this.rarityPercentages[i].length; j++) {
              if (this.rarityPercentages[i][j] == null) {
                atLeastOneMissing = true;
                break;
              }
            }
          }
        }
        return !this.hasMadeGuessInInfiniteMode && atLeastOneMissing;
        */
      },
      continueInExtendedPlay(event) {
        event.preventDefault();
        this.$emit('close');
        this.$emit('continue-in-extended-play');
      },

      async getStatsData() {
        try {
          const localStatsData = localStorage.getItem(`stats_data`);
          if (localStatsData) {
            const potentialStatsData = JSON.parse(localStatsData);
            if (potentialStatsData && potentialStatsData.boardId === this.boardId) {
              this.statsData = potentialStatsData;
              return;
            } else {
              console.warn('Local stats data does not match current board ID, fetching new data.');
            }
          }

          // Get stats data
          const statsResponse = await axios.get(`${config.baseURL}/game/stats/${this.boardId}`);
          this.statsData = statsResponse.data;
          // Save stats data to local storage
          localStorage.setItem(
            `stats_data`,
            JSON.stringify({ ...this.statsData, boardId: this.boardId })
          );
        } catch (error) {
          console.error('Error fetching stats data:', error);
        }
      },

      async setLeaderboardData() {
        try {
          // Check if leaderboard data exists in local storage
          const localLeaderboardData = localStorage.getItem(
            `leaderboard_data_${this.getBoardKey()}`
          );

          if (localLeaderboardData) {
            const parsedData = JSON.parse(localLeaderboardData);
            if (parsedData.rank_for_mode) {
              this.rankData = parsedData;
            }
            return;
          }

          // Fetch leaderboard data if not found in local storage
          let rarities = '';
          for (let i = 0; i < this.rarityPercentages.length; i++) {
            if (!this.rarityPercentages[i]) continue;
            for (let j = 0; j < this.rarityPercentages[i].length; j++) {
              if (this.rarityPercentages[i][j] == null) continue;
              rarities += `&rarity=${this.rarityPercentages[i][j]}`;
            }
          }

          const gim = this.hasMadeGuessInInfiniteMode ? '1' : '0';

          const response = await axios.post(
            `${config.baseURL}/game/stats/${this.boardId}?score=${this.score}&gim=${gim}&flag=0${rarities}`
          );

          const rankData = {
            plays: response.data.plays,
            rank: response.data.rank,
            num_scores: response.data.num_scores,
            rank_for_mode: response.data.rank_for_mode,
            num_scores_for_mode: response.data.num_scores_for_mode,
          };

          this.rankData = rankData;

          // Save this to localStorage element for this board
          const savedData = loadBoardFromLocalStorage(this.getBoardKey());
          if (savedData) {
            saveBoardToLocalStorage(this.getBoardKey(), {
              ...savedData,
              rankData,
            });
          }

          // Save leaderboard data to local storage
          localStorage.setItem(`leaderboard_data_${this.getBoardKey()}`, JSON.stringify(rankData));
        } catch (error) {
          console.error('Error setting leaderboard data:', error);
        }
      },
      setAnswerFrequencyData() {
        try {
          // Check if answer frequency data exists in local storage
          const localAnswerFreqData = localStorage.getItem(`answer_freq_data_${this.boardId}`);

          if (localAnswerFreqData) {
            this.localLiveRarityData = JSON.parse(localAnswerFreqData);
          } else {
            // Save the current value of liveRarityData
            const liveRarityData = this.liveRarityData;

            // Set the local storage item with liveRarityData
            localStorage.setItem(
              `answer_freq_data_${this.boardId}`,
              JSON.stringify(liveRarityData)
            );

            // Update localLiveRarityData with the value from localStorage
            this.localLiveRarityData = liveRarityData;
          }
        } catch (error) {
          console.error('Error setting answer frequency data:', error);
        }
      },
      toggle(index) {
        const i = this.activeIndexes.indexOf(index);
        if (i > -1) {
          this.activeIndexes.splice(i, 1);
        } else {
          this.activeIndexes.push(index);
        }
      },
      isActive(index) {
        return this.activeIndexes.includes(index);
      },
      setActiveTab(tab) {
        this.activeTab = tab;
        if (tab !== 'achievements') {
          localStorage.setItem('hasChangedScoreModalTab', 'true');
          this.hasChangedScoreModalTab = true;
        }
      },

      closeNotification() {
        this.showNotification = false;
      },
    },

    mounted() {
      this.localLiveRarityData = JSON.parse(
        localStorage.getItem(`answer_freq_data_${this.boardId}`)
      );

      this.coordsAndRarities = [];

      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          if (
            i >= this.rarityPercentages.length ||
            this.rarityPercentages[i] == null ||
            j >= this.rarityPercentages[i].length
          ) {
            continue;
          }
          const r = this.rarityPercentages[i][j];
          if (r == null) {
            continue;
          }
          this.coordsAndRarities.push([i, j, r]);
        }
      }
      this.coordsAndRarities.sort((a, b) => a[2] - b[2]);

      Promise.all([this.setLeaderboardData(), this.getStatsData()]).then(() => {
        this.loading = false;
      });
      this.setAnswerFrequencyData();
    },
  };
</script>

<style scoped>
  .modal-content {
    width: 90vw;
    margin: auto;
    margin-top: 32px;
    margin-bottom: 0;
    max-width: 680px;
    background-color: rgba(65, 65, 88, 1);
    border-radius: 16px;
    overflow: hidden;
  }

  .tabs {
    display: flex;
    border-bottom: 2px solid rgba(65, 65, 88, 1);
    margin-bottom: -2px;
  }

  .tab {
    flex: 1;
    height: 42px;
    text-align: center;
    padding: 8px 12px 5px 12px;
    cursor: pointer;
    color: rgba(255, 255, 255, 1);
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;
    border-bottom: 3px solid transparent;
    position: relative;
  }

  .tab:hover {
    background-color: #2a2a3a;
  }

  .tab.close-tab {
    flex: 0;
  }

  .tab.close-tab span {
    font-size: 24px;
  }

  .tab.active {
    color: rgba(147, 147, 236, 1);
    border-bottom-color: rgba(147, 147, 236, 1);
  }

  .tab-content {
    color: #cccccc;
    height: 75vh;
    max-height: 80vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    background-color: rgba(76, 76, 103, 1);

    /* Firefox */
    scrollbar-width: thin; /* opções: auto, thin, none */
    scrollbar-color: rgba(184, 184, 224, 0.7) rgba(255, 255, 255, 0.1);
  }

  /* Chrome, Edge, Safari */
  .tab-content::-webkit-scrollbar {
    width: 8px;
  }

  .tab-content::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  .tab-content::-webkit-scrollbar-thumb {
    background-color: rgba(184, 184, 224, 0.7);
    border-radius: 4px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .tab-content::-webkit-scrollbar-thumb:hover {
    background-color: rgba(120, 120, 200, 1);
  }

  .achievement-icon {
    margin-right: 10px;
    font-size: 20px;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 10px;
  }

  .section-header h3 {
    margin: 0;
    font-size: 16px;
  }

  .collapse-icon {
    color: #8d8f97;
  }

  .gold {
    color: #4040bf; /* Changed from #ffc107 */
  }

  .loading-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .spinner {
    width: 50px;
    height: 50px;
    animation: rotate 2s linear infinite;
  }

  .path {
    stroke: rgba(147, 147, 236, 1);
    stroke-linecap: round;
    animation: dash 1.5s ease-in-out infinite;
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }

  .fade-in {
    animation: fadeIn 0.3s ease-in-out;
    transform-origin: center;
  }

  .tab-emoji {
    display: none;
  }

  .tab-text {
    display: inline;
  }

  .tab-icon {
    display: none;
  }

  .tab-icon svg {
    width: 20px;
    height: 20px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (max-width: 767px) {
    .tab {
      padding: 8px 12px 0 12px;
    }

    .tab.close-tab {
      flex: 0;
      padding-top: 0;
    }

    .tab-icon {
      display: inline-block;
    }

    .tab-text {
      display: none;
    }

    .tab-content {
      height: 700px;
      max-height: 70vh;
    }
  }

  @media (max-width: 360px) {
    .modal-content {
      border-radius: 10px;
    }
  }
</style>
